import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProfile } from '@/hooks/useProfile';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useNativeCamera } from '@/hooks/useNativeCamera';
import { useRevenueCat } from '@/hooks/useRevenueCat';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, Loader2, LogOut, Languages, CreditCard, Camera, Trash2, RefreshCw, ExternalLink, Sun, Moon, Type, ShieldOff } from 'lucide-react';
import { BlockedUsersDialog } from '@/components/BlockedUsersDialog';
import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';
import { useTheme } from 'next-themes';
import { SubscriptionButton } from '@/components/SubscriptionButton';
import { PricingSelection } from '@/components/PricingSelection';
import { WebPricingSelection } from '@/components/WebPricingSelection';
import { compressImage } from '@/utils/imageCompression';
import { LanguageCode } from '@/i18n/translations';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'fr', name: 'Français' },
  { code: 'de', name: 'Deutsch' },
  { code: 'it', name: 'Italiano' },
  { code: 'pt', name: 'Português' },
  { code: 'ru', name: 'Русский' },
  { code: 'ja', name: '日本語' },
  { code: 'ko', name: '한국어' },
  { code: 'zh', name: '中文' },
  { code: 'ar', name: 'العربية' },
  { code: 'hi', name: 'हिन्दी' },
];

const Settings = () => {
  const navigate = useNavigate();
  const { profile, updateProfile } = useProfile();
  const { signOut, subscribed, inTrial, hasHadTrial, subscriptionEnd, checkSubscription } = useAuth();
  const { t, setLanguage, language } = useLanguage();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const { theme, setTheme } = useTheme();
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(
    profile?.preferred_language || language || 'en'
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [restoringPurchases, setRestoringPurchases] = useState(false);
  const [showBlocked, setShowBlocked] = useState(false);
  const [textSize, setTextSize] = useState<'normal' | 'large'>(
    () => (localStorage.getItem('gcc_text_size') as 'normal' | 'large') || 'normal'
  );

  const handleTextSizeChange = useCallback((size: 'normal' | 'large') => {
    setTextSize(size);
    localStorage.setItem('gcc_text_size', size);
    if (size === 'large') {
      document.documentElement.classList.add('large-text');
    } else {
      document.documentElement.classList.remove('large-text');
    }
  }, []);
  const { isNative, pickImage, loading: cameraLoading } = useNativeCamera({ 
    quality: 80, 
    maxWidth: 800, 
    maxHeight: 800 
  });
  const { isIOS, isConfigured, isInitializing, initializationError, customerInfo, restorePurchases } = useRevenueCat();

  // Sync selectedLanguage when profile loads
  useEffect(() => {
    if (profile?.preferred_language) {
      setSelectedLanguage(profile.preferred_language);
    }
  }, [profile?.preferred_language]);

  const handleSaveLanguage = async () => {
    if (!profile) return;

    setSaving(true);
    try {
      await updateProfile({
        preferred_language: selectedLanguage,
      });

      // Update UI language as well
      setLanguage(selectedLanguage as LanguageCode);
      localStorage.setItem('preferredLanguage', selectedLanguage);

      toast({
        title: t('success'),
        description: t('languageHint'),
      });
    } catch (error) {
      toast({
        title: t('error'),
        description: t('tryAgain'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const processAndUploadImage = async (file: File) => {
    if (!profile) return;

    console.log('[Settings] processAndUploadImage called:', file.name, file.type, file.size);

    if (!file.type.startsWith('image/')) {
      console.error('[Settings] Invalid file type:', file.type);
      toast({
        title: t('error'),
        description: t('tryAgain'),
        variant: 'destructive',
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      console.error('[Settings] File too large:', file.size);
      toast({
        title: t('error'),
        description: t('tryAgain'),
        variant: 'destructive',
      });
      return;
    }

    setUploadingImage(true);
    try {
      toast({
        title: t('loading'),
        description: t('loading'),
      });

      console.log('[Settings] Compressing image...');
      const compressedBlob = await compressImage(file, 800, 800, 0.8);
      console.log('[Settings] Compressed size:', compressedBlob.size);

      if (profile.profile_image) {
        const oldPath = profile.profile_image.split('/').slice(-2).join('/');
        console.log('[Settings] Removing old image:', oldPath);
        await supabase.storage.from('profile-images').remove([oldPath]);
      }

      const fileName = `${profile.id}/${Date.now()}.jpg`;
      console.log('[Settings] Uploading to:', fileName);
      const { error: uploadError } = await supabase.storage
        .from('profile-images')
        .upload(fileName, compressedBlob, {
          cacheControl: '3600',
          upsert: true,
        });

      if (uploadError) {
        console.error('[Settings] Upload error:', uploadError);
        throw uploadError;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('profile-images')
        .getPublicUrl(fileName);

      console.log('[Settings] Updating profile with URL:', publicUrl);
      await updateProfile({
        profile_image: publicUrl,
      });

      toast({
        title: t('success'),
        description: t('success'),
      });
    } catch (error: any) {
      console.error('[Settings] Error uploading image:', error?.message, error);
      toast({
        title: t('error'),
        description: error.message || t('tryAgain'),
        variant: 'destructive',
      });
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await processAndUploadImage(file);
  };

  const handleNativeImagePick = async () => {
    try {
      console.log('[Settings] Starting native image pick...');
      const file = await pickImage();
      console.log('[Settings] Image pick result:', file ? `File: ${file.name}, size: ${file.size}` : 'null');
      if (file) {
        await processAndUploadImage(file);
      }
    } catch (error: any) {
      console.error('[Settings] Error picking image:', error?.message, error?.code, error);
      toast({
        title: t('error'),
        description: error?.message || t('tryAgain'),
        variant: 'destructive',
      });
    }
  };

  const handleCancelSubscription = async () => {
    setCancellingSubscription(true);
    try {
      const { data, error } = await supabase.functions.invoke('cancel-subscription');
      
      if (error) throw error;
      
      toast({
        title: t('success'),
        description: t('cancelSubscription'),
      });
      
      await checkSubscription();
    } catch (error: any) {
      console.error('[Settings] Error cancelling subscription:', error);
      toast({
        title: t('error'),
        description: error.message || t('tryAgain'),
        variant: 'destructive',
      });
    } finally {
      setCancellingSubscription(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeletingAccount(true);
    try {
      const { error } = await supabase.functions.invoke('delete-account');
      
      if (error) throw error;
      
      toast({
        title: t('success'),
        description: t('deleteAccount'),
      });
      
      await signOut();
    } catch (error: any) {
      console.error('[Settings] Error deleting account:', error);
      toast({
        title: t('error'),
        description: error.message || t('tryAgain'),
        variant: 'destructive',
      });
    } finally {
      setDeletingAccount(false);
    }
  };

  const handleRestorePurchases = async () => {
    setRestoringPurchases(true);
    try {
      await restorePurchases();
      await checkSubscription();
      toast({
        title: t('restoreSuccess'),
        description: t('restoreSuccess'),
      });
    } catch (error: any) {
      console.error('[Settings] Error restoring purchases:', error);
      toast({
        title: t('error'),
        description: t('tryAgain'),
        variant: 'destructive',
      });
    } finally {
      setRestoringPurchases(false);
    }
  };

  const openAppStoreSubscriptions = () => {
    window.location.href = 'https://apps.apple.com/account/subscriptions';
  };

  return (
    <div className={`${isMobile ? 'safe-area-screen' : 'flex-1'} flex flex-col bg-background overflow-hidden`}>
      <div className="flex-1 overflow-y-auto">
        <div className={`max-w-md mx-auto p-4 ${isMobile ? 'pt-[max(env(safe-area-inset-top),1rem)] pb-[max(env(safe-area-inset-bottom),1rem)]' : 'py-6'}`}>
        <div className="flex items-center gap-2 mb-6">
          {isMobile && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/chats')}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
          )}
          <h1 className="text-2xl font-bold">{t('settings')}</h1>
        </div>

        <div className="space-y-4">
          <Card className="p-6">
            <h2 className="text-lg font-semibold mb-4">{t('profile')}</h2>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <Avatar className="h-20 w-20">
                    <AvatarImage src={profile?.profile_image} />
                    <AvatarFallback className="text-2xl">
                      {profile?.name?.[0]?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <Button
                    variant="secondary"
                    size="icon"
                    className="absolute -bottom-2 -right-2 h-8 w-8 rounded-full"
                    onClick={() => {
                      if (isNative) {
                        handleNativeImagePick();
                      } else {
                        fileInputRef.current?.click();
                      }
                    }}
                    disabled={uploadingImage || cameraLoading}
                  >
                    {uploadingImage || cameraLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Camera className="h-4 w-4" />
                    )}
                  </Button>
                  {!isNative && (
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground">
                    {t('changePhoto')}
                  </p>
                </div>
              </div>
              
              <div className="space-y-2">
                <div>
                  <Label className="text-muted-foreground">{t('name')}</Label>
                  <p className="font-medium">{profile?.name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t('phone')}</Label>
                  <p className="font-medium">{profile?.phone}</p>
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Languages className="h-5 w-5" />
              {t('preferredLanguage')}
            </h2>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="language">{t('preferredLanguage')}</Label>
                <Select
                  value={selectedLanguage}
                  onValueChange={setSelectedLanguage}
                >
                  <SelectTrigger id="language">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LANGUAGES.map((lang) => (
                      <SelectItem key={lang.code} value={lang.code}>
                        {lang.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">
                  {t('languageHint')}
                </p>
              </div>
              {selectedLanguage !== profile?.preferred_language && (
                <Button
                  onClick={handleSaveLanguage}
                  disabled={saving}
                  className="w-full"
                >
                  {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {saving ? t('saving') : t('save')}
                </Button>
              )}
            </div>
          </Card>

          <Card className="p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Sun className="h-5 w-5" />
              {t('appearance')}
            </h2>
            <div className="space-y-2">
              <Label>{t('theme')}</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant={theme === 'light' ? 'default' : 'outline'}
                  className="w-full"
                  onClick={() => setTheme('light')}
                >
                  <Sun className="h-4 w-4 mr-2" />
                  {t('lightMode')}
                </Button>
                <Button
                  variant={theme === 'dark' ? 'default' : 'outline'}
                  className="w-full"
                  onClick={() => setTheme('dark')}
                >
                  <Moon className="h-4 w-4 mr-2" />
                  {t('darkMode')}
                </Button>
              </div>

              <div className="space-y-2 pt-2">
                <Label className="flex items-center gap-2">
                  <Type className="h-4 w-4" />
                  {t('textSize')}
                </Label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant={textSize === 'normal' ? 'default' : 'outline'}
                    className="w-full"
                    onClick={() => handleTextSizeChange('normal')}
                  >
                    <span className="text-sm">A</span>
                    <span className="ml-2">{t('normalText')}</span>
                  </Button>
                  <Button
                    variant={textSize === 'large' ? 'default' : 'outline'}
                    className="w-full"
                    onClick={() => handleTextSizeChange('large')}
                  >
                    <span className="text-base font-semibold">A</span>
                    <span className="ml-2">{t('largeText')}</span>
                  </Button>
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              {t('subscription')}
            </h2>
            <div className="space-y-4">
              {subscribed || inTrial ? (
                <>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">{t('currentPlan')}</Label>
                    <p className="font-medium">
                      {inTrial ? `🎯 ${t('freeTrial')}` : `✅ ${t('subscription')}`}
                    </p>
                  </div>

                  {/* Trial progress bar — "Day X of 3" */}
                  {inTrial && subscriptionEnd && (() => {
                    const msLeft = new Date(subscriptionEnd).getTime() - Date.now();
                    const daysLeft = Math.max(0, Math.ceil(msLeft / (1000 * 60 * 60 * 24)));
                    const currentDay = Math.min(3, Math.max(1, 4 - daysLeft));
                    const progress = Math.round((currentDay / 3) * 100);
                    return (
                      <div className="p-3 rounded-lg bg-primary/5 border border-primary/20 space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-semibold text-primary">
                            {t('trialDayOf').replace('{day}', String(currentDay))}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {daysLeft <= 0
                              ? new Date(subscriptionEnd).toLocaleDateString()
                              : `${daysLeft} day${daysLeft !== 1 ? 's' : ''} left`}
                          </span>
                        </div>
                        <div className="h-1.5 bg-primary/20 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary rounded-full transition-all duration-500"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      </div>
                    );
                  })()}

                  {subscriptionEnd && (
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">
                        {inTrial ? t('trialEnds') : t('subscribedUntil')}
                      </Label>
                      <p className="font-medium">
                        {new Date(subscriptionEnd).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                  {isIOS ? (
                    <>
                      <Button
                        onClick={openAppStoreSubscriptions}
                        variant="outline"
                        className="w-full"
                      >
                        <ExternalLink className="mr-2 h-4 w-4" />
                        {t('manageSubscription')}
                      </Button>
                    </>
                  ) : (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          disabled={cancellingSubscription}
                          variant="outline"
                          className="w-full"
                        >
                          {cancellingSubscription && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                          {t('cancelSubscription')}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>{t('cancelSubscription')}</AlertDialogTitle>
                          <AlertDialogDescription>
                            {t('cancelSubscriptionConfirm')}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
                          <AlertDialogAction onClick={handleCancelSubscription}>
                            {t('confirm')}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    {t('subscriptionBenefits')}
                  </p>
                  {isIOS ? (
                    <PricingSelection />
                  ) : (
                    // WebPricingSelection shows price, trial terms, and auto-renewal
                    // clearly — required for Google Play compliance
                    <WebPricingSelection hasHadTrial={hasHadTrial} />
                  )}
                </div>
              )}
            </div>
          </Card>
          <Card className="p-6">
            <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
              <ShieldOff className="h-5 w-5" />
              Privacy
            </h2>
            <p className="text-sm text-muted-foreground mb-4">
              Manage users you've blocked. Blocked users cannot send you messages.
            </p>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setShowBlocked(true)}
            >
              <ShieldOff className="mr-2 h-4 w-4" />
              Blocked users
            </Button>
          </Card>


          <Button
            variant="destructive"
            className="w-full"
            onClick={signOut}
          >
            <LogOut className="mr-2 h-4 w-4" />
            {t('signOut')}
          </Button>

          <Card className="p-6 border-destructive">
            <h2 className="text-lg font-semibold mb-2 text-destructive flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              {t('deleteAccount')}
            </h2>
            <p className="text-sm text-muted-foreground mb-4">
              {t('deleteAccountConfirm')}
            </p>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="destructive"
                  className="w-full"
                  disabled={deletingAccount}
                >
                  {deletingAccount && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t('deleteAccount')}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('deleteAccount')}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('deleteAccountConfirm')}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDeleteAccount}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    {t('confirm')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </Card>
        </div>
        </div>
      </div>
      <BlockedUsersDialog open={showBlocked} onOpenChange={setShowBlocked} />
    </div>
  );
};

export default Settings;
